import React, { useContext, useEffect, useMemo, useState } from "react";
import { IDFSIContext } from "../types";
import { makeApiRequest } from "../util";
import AuthContext from "./authContext";
import OrgContext from "./orgContext";

const DFSIContext = React.createContext<IDFSIContext>({
  dfsiConnectionData: [],
  setDfsiConnectionData(): void {
    throw new Error("Function not implemented.");
  },
  dfsiChannelData: [],
  setDfsiChannelData(): void {
    throw new Error("Function not implemented.");
  },
  iwfID: undefined,
  allDFSIconnections: [],
});

export const DFSIProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { token } = useContext(AuthContext);
  const { selectedOrg } = useContext(OrgContext);
  // State to hold dfsi connections
  const [dfsiConnectionData, setDfsiConnectionData] = useState<
    { "CONNECTION-ID": string; "DFSI-VERSION": number }[]
  >([]);

  // State to hold dfsi chanel ids
  const [dfsiChannelData, setDfsiChannelData] = useState<string[]>([]);

  // State to hold iwfIDs
  const [iwfID, setIwfID] = useState<string | undefined>(undefined);

  // State to hold all dfis conenction data for the selected org
  const [allDFSIconnections, setAllDFSIconnections] = useState<any[]>([]);

  // fetch IWF-IDs
  const getIWFId = async (orgId: string) => {
    try {
      const response = await makeApiRequest<{
        "IWF-ID": string;
      }>({
        path: `/api/v1/app-config/mappings/${orgId}`,
        token,
        org: orgId,
      });
      setIwfID(response["IWF-ID"]);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // errorAlert(e);
    }
  };

  // fetch all dfsi-connection for the selected org
  const fetchAllDFSIConnections = async () => {
    try {
      if (!selectedOrg?.id) return;

      let page = 1;
      const pageSize = 100;
      let allRows: any[] = [];
      let totalPages = 1;

      do {
        const queryParams = new URLSearchParams({
          page: `${page}`,
          pageSize: `${pageSize}`,
        });

        // eslint-disable-next-line no-await-in-loop
        const response = await makeApiRequest<{
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
          Page: number;
        }>({
          path: `/api/v1/app-config/dfsi-connections?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        allRows = [...allRows, ...response.Rows];
        totalPages = response.NbrOfPages;
        // eslint-disable-next-line no-plusplus
        page++;
      } while (page <= totalPages);

      setAllDFSIconnections(allRows); // Final set after all data is fetched
    } catch (e: any) {
      // errorAlert(e);
    }
  };

  useEffect(() => {
    if (token && selectedOrg?.id) {
      fetchAllDFSIConnections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token && selectedOrg?.id]);

  useEffect(() => {
    if (token && selectedOrg?.id) {
      getIWFId(selectedOrg?.id);
    } else {
      setIwfID(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg?.id]);

  const value = useMemo(
    () => ({
      dfsiConnectionData,
      setDfsiConnectionData,
      dfsiChannelData,
      setDfsiChannelData,
      iwfID,
      allDFSIconnections,
    }),
    [dfsiConnectionData, dfsiChannelData, iwfID, allDFSIconnections]
  );

  return <DFSIContext.Provider value={value}>{children}</DFSIContext.Provider>;
};

export default DFSIContext;
